/**
 * deepLinks.js — routing helpers for incoming universal / app links.
 *
 * Recognised link shapes:
 *   https://greencharge.app/c/GCEV1234567          → /c/GCEV1234567
 *   https://greencharge.app/charge/active          → /charge/active
 *   https://greencharge.app/history/<sessionUid>   → /history/<sessionUid>
 *   greencharge://c/GCEV1234567                    → /c/GCEV1234567
 *   greencharge://open?path=/history                → /history
 *
 * The parser is deliberately restrictive: it maps to a known list of
 * customer-app routes and rejects anything else so a malicious deep
 * link can't force navigation to an unexpected surface (e.g. an
 * outbound URL disguised as a route). Callers should always run their
 * navigation through `resolveDeepLink()` first.
 */

import { CHARGER_CODE_RE } from '../utils/chargerCode.js';

const SESSION_UID_RE  = /^[a-zA-Z0-9._-]{6,64}$/;

// Routes that are safe to enter via a deep link. Anything outside this
// list is bounced to the home screen — no arbitrary path is honoured.
const ALLOWED_PATH_PREFIXES = [
  '/',
  '/auth',
  '/charge',
  '/charge/active',
  '/history',
  '/redeem-codes',
  '/vehicles',
  '/profile',
  '/settings',
  '/support',
  '/notifications',
  '/station',
];

function isAllowed(pathname) {
  return ALLOWED_PATH_PREFIXES.some((prefix) => {
    if (prefix === pathname) return true;
    return pathname.startsWith(`${prefix}/`);
  });
}

function normalisePath(pathname) {
  if (!pathname || typeof pathname !== 'string') return null;
  if (!pathname.startsWith('/')) return null;
  // Strip trailing slash for consistency (but keep root as '/').
  const trimmed = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
  return trimmed;
}

/**
 * Resolve a deep-link URL into a target for the React Router. Returns
 * `{ path, replace }` on success (feed `path` into `navigate(...)`),
 * or `null` when the link is unsupported / invalid.
 *
 * The `replace` flag is `true` when the current history entry should
 * NOT be preserved (cold-start deep links) — see `App.jsx` for how the
 * bootstrap sequence uses it.
 */
export function resolveDeepLink(url) {
  if (typeof url !== 'string' || !url.trim()) return null;
  const trimmed = url.trim();

  let parsed;
  try { parsed = new URL(trimmed); } catch { return null; }

  // Custom scheme: `greencharge://…`. Rewrite to a path we can validate.
  if (parsed.protocol === 'greencharge:' || parsed.protocol === 'gc:') {
    // For `greencharge://c/GCEV…`, `parsed.hostname` = 'c' and
    // `parsed.pathname` = '/GCEV…'.
    const host = parsed.hostname;
    const hostedPath = parsed.pathname;
    if (host === 'c' && hostedPath) {
      const code = hostedPath.replace(/^\/+/, '');
      if (CHARGER_CODE_RE.test(code)) {
        return { path: `/c/${code}`, replace: false };
      }
      return null;
    }
    if (host === 'open') {
      // `greencharge://open?path=/history` — arbitrary internal path,
      // gated by the allow-list below.
      const target = parsed.searchParams.get('path') || '/';
      const path = normalisePath(target);
      if (path && isAllowed(path)) return { path, replace: false };
      return null;
    }
    return null;
  }

  // Universal HTTPS links: only greencharge.app.
  if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
    const host = parsed.hostname.toLowerCase();
    if (host !== 'greencharge.app' && host !== 'www.greencharge.app') return null;
    const path = normalisePath(parsed.pathname);
    if (!path) return null;
    // Special case: `/c/<code>` — validate the code shape.
    if (path.startsWith('/c/')) {
      const code = path.slice(3);
      if (!CHARGER_CODE_RE.test(code)) return null;
      return { path: `/c/${code}${parsed.search || ''}`, replace: false };
    }
    // Special case: `/history/<sessionUid>` — validate the uid shape.
    if (path.startsWith('/history/')) {
      const uid = path.slice('/history/'.length);
      if (!SESSION_UID_RE.test(uid)) return null;
      return { path: `/history/${uid}`, replace: false };
    }
    if (!isAllowed(path)) return null;
    return { path: `${path}${parsed.search || ''}`, replace: false };
  }

  return null;
}
